import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import Button from '../../../components/Button';
import { Checkbox } from 'semantic-ui-react';
import NewUserModal from './NewUserModal';
import axios from 'axios';
import { DropdownOption, ClientUser } from '../types';
import EditRow from './EditRow';
import InfoRow from './InfoRow';
import ConfirmLeaveModal from '../../OrderEntry/ConfirmLeaveModal';
import { useUserContext } from '../../../context/UserContext';

const OuterWrapper = styled.div`
  border: 1px solid #e5e5ea;
  border-radius: 5px;
  margin: 40px auto 70px;
  max-width: 1305px;
`;

const TopHeader = styled.div`
  border: 1px solid #e5e5ea;
`;
const Header = styled.div`
  font-size: 20px;
  font-weight: 700;
`;
const DetailedHeader = styled.div`
  display: grid;
  grid-template-columns: 2% 23% 22% 15% 25%;
  grid-column-gap: 1%;
  border: 1px solid #e5e5ea;
  padding: 30px 32px 28px 32px;
  color: #8e8e93;
  text-transform: uppercase;
`;
const UserInfo = styled.div`
  display: grid;
  grid-template-columns: 2% 23% 22% 15% 25%;
  grid-column-gap: 1%;
  border: 1px solid #e5e5ea;
  padding: 30px 32px 28px 32px;
  position: relative;
`;

interface Props {
  clientId: number;
}

const UsersPage = (props: Props) => {
  const [users, setUsers] = useState<ClientUser[]>([]);
  const [editMode, setEditMode] = useState(-1);
  const [openNewUserModal, setOpenNewUserModal] = useState(false);
  // const [notificationTypes, setNotificationTypes] = useState<DropdownOption[]>([]);
  const [roles, setRoles] = useState<DropdownOption[]>([]);
  const [deleteIndexes, setDeleteIndexes] = useState([]);
  const [noUsersSelected, setNoUsersSelected] = useState(false);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const { user } = useUserContext();

  // const getClientNotificationTypes = async () => {
  //   const { data } = await axios.get('/api/ClientProfile/GetClientNotificationTypes');
  //   const mappedData = data.map(d => ({ value: d.id, label: d.notificationType }));
  //   setNotificationTypes(mappedData);
  // };

  const getRolesForClient = async () => {
    const { data } = await axios.get('/api/ClientProfile/GetRolesForClient');
    const mappedData = data.map((d: any) => ({ value: d.id, label: d.name }));
    setRoles(mappedData);
  };

  const getClientContacts = async () => {
    const { data } = await axios.get<ClientUser[]>(`/api/ClientProfile/getClientContacts/${props.clientId}`);
    data.sort((a, b) => (a.roleOption.value || 2000) - (b.roleOption.value || 2000))
    setUsers(data);
  };

  const afterSavingCall = async () => {
    setEditMode(-1);
    await getClientContacts();
  };

  const deleteContacts = async () => {
    handleDeleteClick();
    if (deleteIndexes.length === 0) {
      return;
    }
    await axios.post(
      '/api/ClientProfile/DeleteContact',
      users.filter((u, index) => deleteIndexes.includes(index))
    );
    setDeleteIndexes([]);
    afterSavingCall();
    setConfirmDeleteModal(false);
  };

  const deleteUserIndexes = (index: number, checked: boolean) => {
    setNoUsersSelected(false);
    if (checked) {
      const newIndexes = [...deleteIndexes, index];
      setDeleteIndexes(newIndexes);
    } else {
      const newIndexes = deleteIndexes.filter(i => i !== index);
      setDeleteIndexes(newIndexes);
    }
  };

  const handleDeleteClick = () => {
    if (deleteIndexes.length > 0) {
      setNoUsersSelected(false);
    } else {
      setNoUsersSelected(true);
    }
    setDeleteIndexes([]);
  };

  const onDelete = () => {
    if (deleteIndexes.length === 0) {
      setNoUsersSelected(true);
      return;
    }
    setConfirmDeleteModal(true);
  };

  useEffect(() => {
    // getClientNotificationTypes();
    getRolesForClient();
  }, []);

  useEffect(() => {
    if (props.clientId) {
      getClientContacts();
    }
  }, [props.clientId]);

  return (
    <OuterWrapper>
      <TopHeader>
        <div style={{ padding: 32, display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Header>Members</Header>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', position: 'relative' }}>
            <div onClick={() => setOpenNewUserModal(true)}>
              <Button buttonText="Add new Member" width="160px" buttonType="ButtonPrimaryMedium" />
            </div>
            <div style={{ marginLeft: 8 }} onClick={onDelete}>
              <Button buttonText="Delete" buttonType="ButtonSecondaryMedium" width="160px" />
              {noUsersSelected && (
                <div style={{ color: '#ff5252', position: 'absolute', bottom: -20 }}>No Users selected</div>
              )}
            </div>
          </div>
        </div>
      </TopHeader>
      <DetailedHeader>
        <div></div>
        <div>Name</div>
        <div>Email</div>
        <div>Role</div>
        <div style={{ color: '#e3e1df', cursor: 'not-allowed' }}>Send Email notification for</div>
      </DetailedHeader>
      { users.map((ua, index, arr) => {
        const singleAdmin =
          ua.roleOption.label === 'Administrator' &&
          arr.filter(
            (u, i) => i !== index && !deleteIndexes.includes(i) && u.roleOption.label === 'Administrator'
          ).length === 0;
        return (
          <UserInfo key={index}>
            <div>
              <Checkbox
                checked={deleteIndexes.includes(index)}
                onChange={(e, { checked }) => deleteUserIndexes(index, checked)}
                disabled={ua.userId === user.id || singleAdmin}
              />
            </div>
            {editMode === index ? (
              <EditRow
                clientuser={ua}
                // notificationTypes={notificationTypes}
                roles={roles}
                afterSavingCall={afterSavingCall}
                singleAdmin={singleAdmin}
              />
            ) : (
              <InfoRow clientUser={ua} onEditClick={() => setEditMode(index)} />
            )}
          </UserInfo>
        );
      })}
      {openNewUserModal && (
        <NewUserModal
          onClose={() => setOpenNewUserModal(false)}
          afterSaveUser={afterSavingCall}
          open={openNewUserModal}
        />
      )}
      {confirmDeleteModal && (
        <ConfirmLeaveModal
          buttonText="Delete"
          header="Are you sure you want to delete"
          onClose={() => setConfirmDeleteModal(false)}
          onConfirmAction={deleteContacts}
          open={confirmDeleteModal}
          cancelButtonText="Cancel"
          info=""
        />
      )}
    </OuterWrapper>
  );
};

export default UsersPage;
